// ── Contact & Organization Info ──────────────────────────────────────────────
export const contactData = {
  address: {
    title: "407 Payal Complex, B/s VSE, Sayajigunj, Vadodara-390005",
    href: "https://www.google.com/maps/place/Imageio+Knowledge+Solutions+Pvt.+Ltd/@22.3085221,73.1858956,17z/data=!3m1!4b1!4m6!3m5!1s0x395fcf36a21bea7d:0x589c449fe52202a2!8m2!3d22.3085221!4d73.1858956!16s%2Fg%2F11h6xr8txj?entry=ttu"
  },
  phone: {
    title: "+91 99248 04021",
    href: "tel:+91 99248 04021"
  },
  email: {
    title: "info@encoraa.com",
    href: "mailto:company.info@mail.com"
  }
};

// ── Pagination Constants & Helpers ───────────────────────────────────────────
export const defaultLimit = 5;
export const defaultPage = 1;
export const getOffset = (page, limit) => (page - 1) * limit;
export const paginationOptions = [5, 10, 20, 50];

// ── Biomass, CO2e & O2 Estimation Constants ──────────────────────────────────
export const DEFAULT_WOOD_DENSITY = 0.5;
export const DEFAULT_FORM_FACTOR = 0.5;
export const DBH_GROWTH_PER_MONTH = 0.05 / 100; // 0.05 cm/month → metres
export const SEEDLING_DBH = 0.005;              // 0.5 cm starting DBH in metres
export const CO2_TO_O2_RATIO = 0.727;           // O2(kg) = CO2e(kg) × 32/44 — mass ratio from photosynthesis stoichiometry

// ── Species Label Resolver ───────────────────────────────────────────────────
export const getSpeciesLabel = (tree) => {
  if (!tree) return 'Unknown Species';
  const commonName = (tree.specie_common_name || tree.common_name || tree.species_common_name || '').trim();
  const scientificName = (tree.tree_species_label || tree.speciesLabel || tree.species_label || tree.label || '').trim();
  if (commonName && scientificName && commonName.toLowerCase() !== scientificName.toLowerCase()) {
    return `${commonName} / ${scientificName}`;
  }
  return commonName || scientificName || tree.treeName || (tree.tree_species ? `Species-${tree.tree_species}` : 'Unknown Species');
};

// ── Allometric Biomass & CO2e Calculator (Chave et al. 2014) ──────────────────
export const calcCO2eFromDBH = (dbh_meters, wd = DEFAULT_WOOD_DENSITY, ff = DEFAULT_FORM_FACTOR) => {
  try {
    if (!dbh_meters || isNaN(dbh_meters) || parseFloat(dbh_meters) <= 0) return 0;
    const D = parseFloat(dbh_meters) * 100; // convert metres to cm
    const WD = (wd && !isNaN(wd) && parseFloat(wd) > 0) ? parseFloat(wd) : DEFAULT_WOOD_DENSITY;
    const FF = (ff && !isNaN(ff) && parseFloat(ff) > 0) ? parseFloat(ff) : DEFAULT_FORM_FACTOR;
    const lnD = Math.log(D);
    const AGB = FF * WD * Math.exp(
      -1.499 + 2.148 * lnD + 0.207 * lnD ** 2 - 0.0281 * lnD ** 3
    );
    return parseFloat((AGB * 0.47 * 3.67).toFixed(4));
  } catch {
    return 0;
  }
};

// ── Per-Tree CO2e at Given Date ──────────────────────────────────────────────
export const getTreeCO2e = (tree, atDate = new Date()) => {
  const treeStartDate = tree?.plantedOn || tree?.createdAt;
  if (!treeStartDate) return 0;

  const treeStart = new Date(treeStartDate);
  if (treeStart > atDate) return 0;

  const knownDBH = tree.dia_breast_height != null &&
    tree.dia_breast_height !== "null" &&
    parseFloat(tree.dia_breast_height) > 0
    ? parseFloat(tree.dia_breast_height) : null;
  const wd = tree.bd_biomass_density;
  const ff = tree.form_factor;

  const now = new Date();
  let dbh;

  if (knownDBH) {
    if (atDate >= now) {
      const monthsPastNow = (atDate - now) / (1000 * 60 * 60 * 24 * 30.44);
      dbh = knownDBH + monthsPastNow * DBH_GROWTH_PER_MONTH;
    } else {
      const totalMonthsToNow = Math.max(0.001, (now - treeStart) / (1000 * 60 * 60 * 24 * 30.44));
      const monthsAlive = Math.max(0, (atDate - treeStart) / (1000 * 60 * 60 * 24 * 30.44));
      const progress = Math.min(1, monthsAlive / totalMonthsToNow);
      dbh = SEEDLING_DBH + progress * (knownDBH - SEEDLING_DBH);
    }
  } else {
    const monthsFromPlanting = Math.max(0, (atDate - treeStart) / (1000 * 60 * 60 * 24 * 30.44));
    dbh = SEEDLING_DBH + monthsFromPlanting * DBH_GROWTH_PER_MONTH;
  }

  dbh = Math.max(SEEDLING_DBH, dbh);
  return calcCO2eFromDBH(dbh, wd, ff);
};

// ── Cumulative Monthly CO2 & O2 Chart Data Builder ───────────────────────────
export const buildCumulativeCO2ChartData = (trees) => {
  if (!trees || trees.length === 0) return null;

  const now = new Date();

  let globalStart = null;
  let totalCurrentCO2e = 0;
  let totalCurrentO2 = 0;
  let treesWithDBH = 0;

  trees.forEach(tree => {
    const d = tree.plantedOn || tree.createdAt;
    if (d) {
      const date = new Date(d);
      if (!globalStart || date < globalStart) globalStart = date;
    }
    const hasDBH = tree.dia_breast_height != null &&
      tree.dia_breast_height !== "null" &&
      parseFloat(tree.dia_breast_height) > 0;
    if (hasDBH) treesWithDBH++;

    const treeCO2 = getTreeCO2e(tree, now);
    totalCurrentCO2e += treeCO2;
    totalCurrentO2 += treeCO2 * CO2_TO_O2_RATIO;
  });

  if (!globalStart) return null;

  const start = new Date(globalStart.getFullYear(), globalStart.getMonth(), 1);

  const labels = [];
  let cursor = new Date(start);
  while (cursor <= now) {
    labels.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const data = labels.map(label => {
    if (label === currentMonthStr) {
      return parseFloat(totalCurrentCO2e.toFixed(2));
    }
    const [yr, mo] = label.split("-").map(Number);
    const endOfMonthDate = new Date(yr, mo, 0, 23, 59, 59);

    let monthSum = 0;
    trees.forEach(tree => {
      monthSum += getTreeCO2e(tree, endOfMonthDate);
    });
    return parseFloat(monthSum.toFixed(2));
  });

  const o2Data = data.map((co2, i) => {
    if (i === data.length - 1) {
      return parseFloat(totalCurrentO2.toFixed(2));
    }
    return parseFloat((co2 * CO2_TO_O2_RATIO).toFixed(2));
  });

  return {
    labels,
    data,
    o2Data,
    totalCO2e: parseFloat(totalCurrentCO2e.toFixed(2)),
    totalO2: parseFloat(totalCurrentO2.toFixed(2)),
    treesWithDBH,
    totalTrees: trees.length
  };
};

// ── Default Placeholder Chart Generator ──────────────────────────────────────
export const getDefaultChartData = (label = "No Data") => ({
  labels: ["No Data"],
  datasets: [{
    label,
    data: [0],
    borderColor: "#e0e0e0",
    backgroundColor: "rgba(224, 224, 224, 0.1)",
    tension: 0.1
  }]
});

// ── Number & Weight Metric Formatter ──────────────────────────────────────────
export const formatMetricValue = (val, unit = 'kg') => {
  if (val == null || isNaN(val)) return `0.00 ${unit}`;
  const num = parseFloat(val);
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)} t`;
  }
  return `${num.toFixed(2)} ${unit}`;
};

// ── Image Base64 Converter for PDF Export ─────────────────────────────────────
export const getBase64ImageFromUrl = async (imageUrl) => {
  if (!imageUrl) return null;
  let url = imageUrl;
  if (url.startsWith('/')) {
    url = (typeof window !== 'undefined' ? window.location.origin : '') + url;
  }
  const cacheBuster = (url.includes('?') ? '&' : '?') + '_cb=' + new Date().getTime();
  const fetchUrl = url + cacheBuster;
  try {
    const res = await fetch(fetchUrl, { method: 'GET', mode: 'cors' });
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn(`Failed to fetch image for PDF: ${url}`, err);
    return null;
  }
};

export default {
  contactData,
  defaultLimit,
  defaultPage,
  getOffset,
  paginationOptions,
  DEFAULT_WOOD_DENSITY,
  DEFAULT_FORM_FACTOR,
  DBH_GROWTH_PER_MONTH,
  SEEDLING_DBH,
  CO2_TO_O2_RATIO,
  getSpeciesLabel,
  calcCO2eFromDBH,
  getTreeCO2e,
  buildCumulativeCO2ChartData,
  getDefaultChartData,
  formatMetricValue,
  getBase64ImageFromUrl
};